import React, { useContext } from 'react';
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { getProductImage } from '@components/cart/ProductImage';
import { Context } from '@components/Context';
import CodeCopy from '@components/shared/CodeCopy';
import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import SimpleBox from '@components/shared/SimpleBox';
import { ProductCode, PromotionObject } from '@contracts';
import { getProductLink, getProductTitle } from '@data/products';
import { useCountry } from '@hooks/useCountry';
import { isPromotionActive } from '@utils/contextHelpers';
import { getFromLocalStorage } from '@utils/localStorage';

interface ProductPromotionProps {
  productId: string;
}

const ProductPromotion: React.FC<ProductPromotionProps> = ({ productId }) => {
  const { state } = useContext(Context);
  const { currency } = state;
  const { t } = useTranslation();
  const { country } = useCountry();
  const promotions: PromotionObject[] = getFromLocalStorage('promotions', true) ?? [];

  const currentPromotion = promotions.find((promo) => {
    return isPromotionActive(promo, country, productId);
  });

  if (!currentPromotion) {
    return null;
  }

  if (currentPromotion.freeGift) {
    const freeGiftProductCode = currentPromotion.freeGift as ProductCode;
    const image = getProductImage(freeGiftProductCode, country, 1);
    const freeGiftProductTitle = getProductTitle(freeGiftProductCode);
    const freeGiftLink = getProductLink(freeGiftProductCode);
    const productTitle = getProductTitle(productId);
    const priceText = currentPromotion.spendRequirement ? `${currency.symbol}${currentPromotion.spendRequirement[country]?.toString()}` : '';

    return (
      <PromotionContainer>
        <SimpleBox badgeText={t('components.limited-offer') ?? ''} isHighlighted badgeType="recommended">
          <PromotionContent>
            <PromotionImage src={image} alt={freeGiftProductTitle} />
            <div>
              {currentPromotion.couponCode ? (
                <>
                  <p>
                    <Trans
                      i18nKey="components.promotion-pdp-text-with-code"
                      values={{
                        freeGiftLink,
                        freeGiftTitle: freeGiftProductTitle,
                        purchasedProduct: productTitle
                      }}
                      components={{ a: <LinkTranslated /> }}
                    />
                  </p>
                  <CodeCopy code={currentPromotion?.couponCode ?? ''} />
                </>
              ) : (
                <>
                  <p>
                    {currentPromotion.spendRequirement ? (
                      <Trans
                        i18nKey="components.promotion-pdp-spend"
                        values={{
                          freeGiftLink,
                          freeGiftTitle: freeGiftProductTitle,
                          priceText
                        }}
                        components={{ a: freeGiftLink ? <LinkTranslated /> : <span></span> }}
                      />
                    ) : (
                      <Trans
                        i18nKey="components.promotion-pdp-text"
                        values={{
                          freeGiftLink,
                          freeGiftTitle: freeGiftProductTitle,
                          purchasedProduct: productTitle
                        }}
                        components={{ a: <LinkTranslated /> }}
                      />
                    )}
                  </p>
                </>
              )}
            </div>
          </PromotionContent>
        </SimpleBox>
      </PromotionContainer>
    );
  }

  if (currentPromotion.couponCode) {
    return (
      <PromotionContainer>
        <SimpleBox badgeText={t('components.limited-offer') ?? ''} isHighlighted badgeType="recommended">
          <div>
            <p>{currentPromotion.description}</p>
            <CodeCopy code={currentPromotion?.couponCode ?? ''} />
          </div>
        </SimpleBox>
        <PromoTermsText></PromoTermsText>
      </PromotionContainer>
    );
  }

  return null;
};

export default ProductPromotion;

const PromotionContainer = styled.div`
  width: 100%;

  & .simple-box {
    border-color: ${Colors.paleGold};
  }
`;

const PromotionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  p {
    font-family: 'BrownStd';
    font-size: 16px;
    font-weight: 400;
    line-height: 140%;
    color: ${Colors.navyBlue};

    a {
      color: ${Colors.lilac};
      font-weight: 700;
    }

    @media (max-width: 1300px) {
      font-size: 14px;
    }
  }
`;

const PromotionImage = styled.img`
  width: 50px;

  @media (max-width: 1300px) {
    display: none;
  }
`;

const PromoTermsText = styled.div`
  opacity: 0.5;
  margin-top: 10px;
`;
